import { EraClaimSelection } from 'components/lab/billingEraReports/types'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { BillingArchiveEraClaimsResult, BillingTransactionClaim, EraClaimArchiveFailure } from 'store/types'

// Selection resets whenever `resetKey` changes (tab, filters, page).
export const useEraClaimSelection = (resetKey: string) => {
  const [claimsByTransaction, setClaimsByTransaction] = useState<Record<string, BillingTransactionClaim[]>>({})
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [failures, setFailures] = useState<Map<string, EraClaimArchiveFailure>>(new Map())

  useEffect(() => {
    setSelectedIds(new Set())
    setFailures(new Map())
  }, [resetKey])

  const archivableClaims = useMemo(() => Object.values(claimsByTransaction).flat(), [claimsByTransaction])

  // Only claims currently on screen count, so a stale id never gets archived.
  const selectedClaims = useMemo(
    () => archivableClaims.filter((c) => selectedIds.has(c.id)),
    [archivableClaims, selectedIds],
  )

  const getEraClaimIds = useCallback(
    (eraId: string) => archivableClaims.filter((c) => c.eraId === eraId).map((c) => c.id),
    [archivableClaims],
  )

  const reportClaims = useCallback((transactionId: string, claims: BillingTransactionClaim[] | null) => {
    setClaimsByTransaction((prev) => {
      const { [transactionId]: _, ...rest } = prev
      return claims ? { ...rest, [transactionId]: claims } : rest
    })
  }, [])

  // Failed claims that can still be archived stay selected; the rest are only marked.
  const applyArchiveResult = useCallback((result: BillingArchiveEraClaimsResult) => {
    setSelectedIds(new Set(result.failed.map((f) => f.claimId)))
    setFailures(new Map(result.failed.map((f) => [f.claimId, f.reason])))
  }, [])

  const toggleRow = useCallback((claimId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(claimId)) {
        next.delete(claimId)
      } else {
        next.add(claimId)
      }
      return next
    })
  }, [])

  const toggleEra = useCallback(
    (eraId: string) => {
      const ids = getEraClaimIds(eraId)

      setSelectedIds((prev) => {
        const next = new Set(prev)
        const anySelected = ids.some((id) => next.has(id))
        ids.forEach((id) => (anySelected ? next.delete(id) : next.add(id)))
        return next
      })
    },
    [getEraClaimIds],
  )

  const selection = useMemo<EraClaimSelection>(
    () => ({
      selectedIds: new Set(selectedClaims.map((c) => c.id)),
      toggleRow,
      toggleEra,
      getEraClaimIds,
      reportClaims,
      failures,
    }),
    [selectedClaims, toggleRow, toggleEra, getEraClaimIds, reportClaims, failures],
  )

  return { selection, selectedClaims, applyArchiveResult }
}
